import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, PackageMinus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { ItemRow } from './ItemRow';
import { DeliveryHeader, PoLinesSummary } from './DeliveryInfo';
import { errMsg, fmtQ, poUsage, toItems } from './srUtils';

const problemOf = (chosen, picks, lines, usage) => {
  for (const it of chosen) {
    const p = picks[it.key];
    if (it.max != null && Number(p.qty) > it.max + 1e-6) return `${it.title}: only ${fmtQ(it.max)} pcs can go back`;
    if ((p.reason || 'EXCESS') === 'OTHER' && (p.note || '').trim().length < 3) return `${it.title}: type the reason for 'Other'`;
  }
  const over = (lines || []).find((l) => (usage[l.po_item_id] || 0) > l.available_qty + 1e-6);
  return over ? `${over.po_number} ${over.supplier_design_name}: only ${fmtQ(over.available_qty)} ${over.unit} above the PO can go back` : '';
};

/** Step 2 — type the pcs going back per item, pick a reason, raise the return (stock is held right away). */
export const ReturnForm = ({ section, entryId, refreshKey, onRaised }) => {
  const [d, setD] = useState(null);
  const [picks, setPicks] = useState({});
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setD(null); setPicks({}); setRemarks('');
    apiClient.get('/supplier-returns/delivery', { params: { section, entry_id: entryId } })
      .then((r) => setD(r.data)).catch((e) => { setD(false); toast.error(errMsg(e, 'Failed to load the delivery')); });
  }, [section, entryId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const items = useMemo(() => (d ? toItems(d) : []), [d]);
  const chosen = items.filter((it) => Number(picks[it.key]?.qty) > 0);
  const total = chosen.reduce((s, it) => s + Number(picks[it.key].qty), 0);
  const usage = d?.section === 'Greige' ? poUsage(items, picks) : null;
  const setPick = (key, patch) => setPicks((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

  const submit = async () => {
    if (!chosen.length) return toast.error('Type the pcs going back for at least one item');
    const problem = problemOf(chosen, picks, d.po_lines, usage || {});
    if (problem) return toast.error(problem, { duration: 8000 });
    setSaving(true);
    try {
      const lines = chosen.map((it) => ({ ...it.ref, qty: Number(picks[it.key].qty), reason: picks[it.key].reason || 'EXCESS', note: picks[it.key].note || '' }));
      const r = await apiClient.post('/supplier-returns', { section, entry_id: entryId, lines, remarks });
      toast.success(`${r.data.return_no} raised — ${fmtQ(r.data.total_qty)} pcs held until it is approved`);
      onRaised(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not raise the return'), { duration: 9000 });
    } finally { setSaving(false); }
  };

  if (d === null) return <Loader2 className="w-6 h-6 animate-spin text-[#B45309] mx-auto" />;
  if (!d) return <p className="text-sm text-red-600 text-center py-6">Could not load this delivery.</p>;
  return (
    <div className="space-y-3" data-testid="sr-return-form">
      <DeliveryHeader d={d} />
      <PoLinesSummary lines={d.po_lines} usage={usage} />
      {d.document?.force_closed && <p className="text-xs text-amber-800">This document was force-closed.</p>}
      {items.length === 0 ? <p className="text-sm text-gray-500 text-center py-6" data-testid="sr-no-items">No items on this delivery.</p>
        : <div className="space-y-2">{items.map((it) => <ItemRow key={it.key} it={it} pick={picks[it.key]} onChange={(patch) => setPick(it.key, patch)} />)}</div>}
      <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks for the supplier (optional)" rows={2} data-testid="sr-remarks" />
      <Button onClick={submit} disabled={saving || !chosen.length} className="w-full h-12 bg-[#B45309] hover:bg-[#92400E] disabled:opacity-100 disabled:bg-[#E5DFD0] disabled:text-gray-600 text-base sticky bottom-3 shadow-lg" data-testid="sr-submit-btn">
        {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <PackageMinus className="w-5 h-5 mr-2" />}
        {chosen.length ? `Raise return · ${fmtQ(total)} pcs from ${chosen.length} item(s)` : 'Type the pcs going back'}
      </Button>
    </div>
  );
};
